import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { getCaseStudies, CASE_STUDY_CATEGORIES } from '@/data/caseStudies';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';

const DIVISIONS = ['operations', 'people', 'growth', 'launch'];

// Section 8 — up to four genuine case studies, one per service area, from
// the SAME data source as the main Case Studies page. If a division has no
// genuine published case study, fewer cards render — nothing is fabricated.
export const ServicesCaseStudies = () => {
  useLiveTick();
  const picks = DIVISIONS.map((div) =>
    getCaseStudies().find((cs) => cs.status === 'published' && cs.categories.includes(div))
  ).filter(Boolean);

  return (
    <section aria-labelledby="services-cases-heading" data-testid="services-case-studies" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="flex items-center gap-3">
              <span className="font-mono text-xs font-medium tracking-widest text-gold-700">07</span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
                Client Results
              </span>
            </p>
            <h2 id="services-cases-heading" data-testid="services-cases-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
              See how Amden supports businesses
            </h2>
          </div>
          <Link
            to="/case-studies"
            data-testid="services-cases-view-all"
            className="group inline-flex shrink-0 items-center gap-2 text-sm font-bold text-navy-900 transition-colors hover:text-gold-700"
          >
            View All Case Studies
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {picks.map((cs, i) => {
            const category = CASE_STUDY_CATEGORIES[cs.categories[0]];
            return (
              <Reveal key={cs.id} delay={(i % 4) * 0.06} className="h-full">
                <article
                  data-testid={`services-case-${cs.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift"
                >
                  {cs.image && (
                    <div className="overflow-hidden">
                      <img
                        src={cs.image.src}
                        alt={cs.image.alt}
                        width="1080"
                        height="675"
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-2">
                      {category && (
                        <span
                          className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
                          style={{ backgroundColor: category.soft, color: category.dark }}
                        >
                          {category.label}
                        </span>
                      )}
                      <span className="text-xs font-semibold text-slate-400">{cs.client}</span>
                    </div>
                    <h3 className="mt-3 text-base font-extrabold leading-snug tracking-tight text-navy-900">
                      {cs.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">
                      {cs.summary}
                    </p>
                    {cs.outcomes.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {cs.outcomes.slice(0, 2).map((o) => (
                          <li key={o} className="flex items-start gap-2 text-xs font-medium text-navy-800">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold-700" aria-hidden="true" />
                            {o}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to={`/case-studies/${cs.slug}`}
                      data-testid={`services-case-${cs.slug}-link`}
                      className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-bold text-navy-900 transition-colors hover:text-gold-700"
                    >
                      View Case Study
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
